import {
    TextField,
    TextAreaField,
    SelectField,
    CheckboxField,
    SwitchField,
    Button,
    Flex,
    Text,
} from "@aws-amplify/ui-react";
import { ENGAGEMENT_TYPES, FEE_TYPES, FEE_LABELS, LIMITS, NO_RATE_LABEL } from "./listingModel";

// Stateless field group: fee / engagement / visibility.
// Emits onChange(name, value); engagementTypes is emitted as a new array.
export function DealFields({ values, errors, onChange }) {
    const engagement = Array.isArray(values.engagementTypes) ? values.engagementTypes : [];
    const toggleEngagement = (type, checked) => {
        const next = checked
            ? engagement.includes(type) ? engagement : [...engagement, type]
            : engagement.filter((t) => t !== type);
        onChange("engagementTypes", next);
    };
    const isPercent = values.feeType === "percent";

    return (
        <Flex direction="column" gap="15px">
            {/* Price is optional (§1.2): leaving both parts blank means "Rate on request". */}
            <SelectField
                label="Fee type"
                name="feeType"
                value={values.feeType || ""}
                hasError={!!errors.feeType}
                errorMessage={errors.feeType}
                onChange={(e) => onChange("feeType", e.target.value)}
            >
                <option value="">No price / rate on request</option>
                {FEE_TYPES.map((t) => (
                    <option key={t} value={t}>
                        {FEE_LABELS[t] || t}
                    </option>
                ))}
            </SelectField>
            <TextField
                label={isPercent ? "% share" : "$ amount"}
                name="feeAmount"
                type="number"
                min={0}
                step="0.01"
                value={values.feeAmount ?? ""}
                descriptiveText={`Leave blank to show “${NO_RATE_LABEL}”`}
                hasError={!!errors.feeAmount}
                errorMessage={errors.feeAmount}
                onChange={(e) => onChange("feeAmount", e.target.value)}
            />
            <TextField
                label="Rate note (optional)"
                name="feeNote"
                maxLength={LIMITS.feeNote}
                value={values.feeNote || ""}
                descriptiveText="Ranges, what is included, negotiable"
                hasError={!!errors.feeNote}
                errorMessage={errors.feeNote}
                onChange={(e) => onChange("feeNote", e.target.value)}
            />
            <Flex direction="column" gap="5px">
                <Text>Engagement types</Text>
                <Flex direction="row" wrap="wrap" gap="15px">
                    {ENGAGEMENT_TYPES.map((t) => (
                        <CheckboxField
                            key={t}
                            label={t}
                            name="engagementTypes"
                            value={t}
                            checked={engagement.includes(t)}
                            onChange={(e) => toggleEngagement(t, e.target.checked)}
                        />
                    ))}
                </Flex>
                {errors.engagementTypes && (
                    <Text color="red" fontSize="small">
                        {errors.engagementTypes}
                    </Text>
                )}
            </Flex>
            <SelectField
                label="Visibility"
                name="visibility"
                value={values.visibility || "public"}
                hasError={!!errors.visibility}
                errorMessage={errors.visibility}
                onChange={(e) => onChange("visibility", e.target.value)}
            >
                <option value="public">Public</option>
                <option value="private">Private (only me)</option>
            </SelectField>
            <SwitchField
                label="Show on 1stinfluencer.com/collabs and the home page"
                name="showOnLanding"
                isChecked={!!values.showOnLanding}
                isDisabled={values.visibility !== "public"}
                onChange={(e) => onChange("showOnLanding", e.target.checked)}
            />
        </Flex>
    );
}

// Stateless contact group: one free-form list that IS the public contact block (§1.1).
// There is nothing to opt into any more — every line typed here is published as written, and a
// contact that is not in the list simply does not exist on the public page.
export function ContactFields({ values, errors, onChange, accountEmail }) {
    const contacts = Array.isArray(values.contacts) ? values.contacts : [];
    const filled = contacts.map((c) => String(c || "").trim()).filter(Boolean);
    const email = String(accountEmail || "").trim();
    const emailListed = filled.some((c) => c.toLowerCase() === email.toLowerCase());
    // Append after any trailing blank lines the author left behind, so the click never inserts a gap.
    const addAccountEmail = () => {
        const next = contacts.slice();
        while (next.length && !String(next[next.length - 1] || "").trim()) next.pop();
        next.push(email);
        onChange("contacts", next);
    };

    return (
        <Flex direction="column" gap="15px">
            <TextAreaField
                label="Public contact lines (one per line)"
                name="contacts"
                rows={4}
                value={contacts.join("\n")}
                placeholder={"hello@brand.com\nWhatsApp +1 415 555 0100\nTelegram @founder"}
                descriptiveText={`Everything you write here is published on your public listing page. One contact per line, up to ${LIMITS.contacts} lines of ${LIMITS.contactLine} characters. Any kind of contact works: an email, a WhatsApp link, a Telegram handle, a phone number, a booking link.`}
                hasError={!!errors.contacts}
                errorMessage={errors.contacts}
                onChange={(e) => onChange("contacts", e.target.value.split("\n"))}
            />
            {email && (
                <Flex direction="row">
                    <Button size="small" isDisabled={emailListed} onClick={addAccountEmail}>
                        Add my account email
                    </Button>
                </Flex>
            )}
            {values.visibility === "public" && !filled.length && (
                <Text fontSize="small" color="font.tertiary">
                    No contact will be published: people will only be able to reach you through the
                    links in your listing.
                </Text>
            )}
        </Flex>
    );
}
